import React from 'react';
import './OrderSummary.scss';

/**
 * Renders orders section associated with an account
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const OrderSummary = ({ customer }) => {
  const { order } = customer;
  const unshippedLineItems = [];
  const trackingData = {};

  // Sort and store line items based on shipping status
  order.lineItems.forEach((lineItem) => {
    if (lineItem.fulfillment.fulfillment === true && lineItem.fulfillment.trackingNumber) {
      if (trackingData[lineItem.fulfillment.trackingNumber]) {
        trackingData[lineItem.fulfillment.trackingNumber].push(lineItem);
      } else {
        trackingData[lineItem.fulfillment.trackingNumber] = [lineItem];
      }
    } else {
      unshippedLineItems.push(lineItem);
    }
  });

  /**
   * Render line items
   * @param lineItems
   * @returns {*}
   */
  const renderLineItems = (lineItems) => {
    return lineItems.map((lineItem, i) => {
      return (
        <tr
          key={`line-item-${i}`}
          className="c-order-summary__row c-order-summary__row--item body-2"
        >
          <td className="c-order-summary__cell c-order-summary__cell-item-url" data-label="ITEM">
            <div className="c-order-summary__cell-item-image-container">
              <a href={lineItem.productURL}>
                <img src={lineItem.imageURL} alt={lineItem.imageAlt} />
              </a>
            </div>
            <div className="c-order-summary__cell-item-text-container">
              <a href={lineItem.productURL}>
                <p>{lineItem.title}</p>
              </a>
              <p className="c-order-summary__cell-item-mobile-price" data-label="QUANTITY X PRICE">
                {lineItem.quantity} X {lineItem.price}
              </p>
            </div>
          </td>
          <td className="c-order-summary__cell desktop-only" data-label="PRICE">
            {lineItem.price}
          </td>
          <td className="c-order-summary__cell desktop-only" data-label="QUANTITY">
            {lineItem.quantity}
          </td>
          <td className="c-order-summary__cell desktop-only" data-label="TOTAL">
            {lineItem.total}
          </td>
        </tr>
      );
    });
  };

  /**
   * Render table to display line items
   * @param lineItems
   * @returns {*}
   */
  const renderTable = (lineItems) => {
    if (lineItems.length > 0) {
      const sectionTracking = [
        lineItems[0].fulfillment.trackingNumber,
        lineItems[0].fulfillment.trackingURL,
      ];

      return (
        <div className="c-order-summary__section-table-container">
          {sectionTracking[0] && (
            <div className="c-order-summary__section-shipping-header">
              <h4 className="c-order-summary__section-heading eyebrow-2">TRACK SHIPMENT</h4>
              <p>
                <a href={sectionTracking[1]}>{sectionTracking[0]}</a>
              </p>
            </div>
          )}
          <table className="c-order-summary__list">
            <thead>
              <tr className="c-order-summary__row c-order-summary__row--header eyebrow-2">
                <th className="c-order-summary__cell c-order-summary__cell--header">ITEM</th>
                <th className="c-order-summary__cell c-order-summary__cell--header">PRICE</th>
                <th className="c-order-summary__cell c-order-summary__cell--header">QUANTITY</th>
                <th className="c-order-summary__cell c-order-summary__cell--header">TOTAL</th>
              </tr>
            </thead>
            <tbody>{renderLineItems(lineItems)}</tbody>
          </table>
        </div>
      );
    }
  };

  /**
   * Generate tables for each tracking number
   * @returns {*}
   */
  const renderShippedSections = () => {
    return Object.keys(trackingData).map((trackingNumber) => {
      return renderTable(trackingData[trackingNumber]);
    });
  };

  return (
    <div className="c-order-summary background-color-gray">
      <h5 className="c-order-summary__heading h3-roobert">Order History</h5>
      <div className="c-order-summary__section body-2">
        <div className="c-order-summary__section-block">
          <h4 className="c-order-summary__section-heading eyebrow-2">ORDER</h4>
          <p>{order.name}</p>
        </div>
        <div className="c-order-summary__section-block">
          <h4 className="c-order-summary__section-heading eyebrow-2">DATE</h4>
          <p>{order.createdAt}</p>
          {order.cancelled === true && (
            <p>
              Order Cancelled on {order.cancelledAt}
              <br />
              {order.cancelledReason}
            </p>
          )}
        </div>
        <div className="c-order-summary__section-block section-large">
          <h4 className="c-order-summary__section-heading eyebrow-2">SHIPPING ADDRESS</h4>
          <div dangerouslySetInnerHTML={{ __html: order.shippingAddress }}></div>
        </div>
        <div className="c-order-summary__section-block section-large">
          <h4 className="c-order-summary__section-heading eyebrow-2">BILLING ADDRESS</h4>
          <div dangerouslySetInnerHTML={{ __html: order.billingAddress }}></div>
        </div>
      </div>
      <div className="c-order-summary__section body-2">
        {renderTable(unshippedLineItems)}
        {renderShippedSections()}
      </div>
      <div className="c-order-summary__section body-2">
        <div className="c-order-summary__section-totals">
          <div className="c-order-summary__section-subtotals">
            <div className="c-order-summary__section-totals-row">
              <h4 className="c-order-summary__section-heading eyebrow-2">SUBTOTAL</h4>
              <p>{order.subtotal}</p>
            </div>
            <div className="c-order-summary__section-totals-row">
              <h4 className="c-order-summary__section-heading eyebrow-2">DISCOUNTS</h4>
              <p>{order.discounts}</p>
            </div>
            <div className="c-order-summary__section-totals-row">
              <h4 className="c-order-summary__section-heading eyebrow-2">SHIPPING</h4>
              <p>{order.shipping}</p>
            </div>
            <div className="c-order-summary__section-totals-row">
              <h4 className="c-order-summary__section-heading eyebrow-2">TAX</h4>
              <p>{order.taxes}</p>
            </div>
          </div>
          <div className="c-order-summary__section-totals-row c-order-summary__section-totals-totalprice">
            <h4 className="c-order-summary__section-heading eyebrow-2">TOTAL</h4>
            <p>{order.total}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
